import {
  ApplicationRef,
  Injectable,
  Injector,
  ComponentRef,
  EmbeddedViewRef,
  ComponentFactoryResolver,
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { AmKeyboardComponent } from './am-keyboard.component';
import { Observable, Subject } from 'rxjs';

/**
  Servicio para manejar la lógica de un teclado virtual personalizado.
  Este servicio permite mostrar, ocultar y destruir un teclado virtual, así como
  gestionar su estado y comportamiento en la interfaz de usuario.
**/
@Injectable({
  providedIn: 'root',
})
export class AmKeyboardService {
  /**
    Referencia al componente del teclado virtual.
    @type {ComponentRef<AmKeyboardComponent> | undefined}
  **/
  keyboardRef: ComponentRef<AmKeyboardComponent> | undefined;

  /**
      Elemento raíz del DOM donde se inserta el teclado virtual.
      @type {HTMLElement}
      @private
    **/
  private divRoot: HTMLElement;

  /**
      Estado interno que indica si el teclado está visible.
      @type {boolean}
      @private
    **/
  private _isShow = false;

  /**
      Subject para emitir cambios en el estado de visibilidad del teclado.
      @type {Subject<boolean>}
      @public
    **/
  public isShow$: Subject<boolean> = new Subject<boolean>();

  /**
      Lista de tipos de entrada aceptados por el teclado virtual.
      @type {string[]}
      @public
    **/
  public acceptList = ['number', 'text', 'password', 'calendar', 'phone'];
  constructor(
    private componentFactoryResolver: ComponentFactoryResolver,
    private injector: Injector,
    private appRef: ApplicationRef
  ) {}
  /**
    Obtiene un observable que emite el estado de visibilidad del teclado.
    @return {Observable<boolean>} Observable que emite `true` si el teclado está visible y `false` si no lo está.
  **/
  getStatusKeyboard(): Observable<boolean> {
    return this.isShow$.asObservable();
  }
  /**
    Obtiene el estado actual de visibilidad del teclado.
    @return {boolean} `true` si el teclado está visible, `false` si no lo está.
  **/
  get isShow(): boolean {
    return this._isShow;
  }
  set isShow(keyboardActive: boolean) {
    if (this._isShow === keyboardActive) {
      return;
    }
    this._isShow = keyboardActive;
    this.isShow$.next(keyboardActive);
  }

  /**
    Destruye el teclado virtual y limpia los recursos asociados.
    @param {boolean} notify - Indica si se debe notificar el cambio de estado (opcional, por defecto `false`).
  **/
  destroy(notify = false): void {
    if (!this.keyboardRef) {
      if (notify) {
        this.isShow = false;
      }
      return;
    }
    const { isFloat, inputReference } = this.keyboardRef.instance;
    inputReference?.classList.remove('input-onfocus');
    this.keyboardRef.destroy();
    this.keyboardRef = undefined;
    if (this.divRoot) {
      if (isFloat) {
        this.divRoot.remove();
      } else {
        this.divRoot.style.removeProperty('display');
      }
      this.divRoot = undefined;
    }
    this.isShow = false;
  }

  /**
    Muestra el teclado virtual y lo asocia a un elemento de entrada específico.
    @param {string} type - Tipo de entrada (ej. 'text', 'number', etc.).
    @param {HTMLElement} inputReference - Elemento del DOM al que se asocia el teclado.
    @param {UntypedFormControl} currentControl - Control de formulario asociado al elemento de entrada.
    @param {boolean} showNavigateBar - Indica si se debe mostrar la barra de navegación (opcional, por defecto `false`).
    @param {string} nameList - Lista de nombres para personalizar el teclado (opcional, por defecto `''`).
    @param {boolean} isFloat - Indica si el teclado debe flotar sobre la interfaz (opcional, por defecto `true`).
    @param {boolean} isDarkTheme - Indica si el teclado debe usar un tema oscuro (opcional, por defecto `false`).
    @param {string} contentKeyboard - ID del contenedor donde se mostrará el teclado (opcional).
    @return void
  **/
  show(
    type: string,
    inputReference: HTMLElement,
    currentControl: UntypedFormControl,
    showNavigateBar = false,
    nameList = '',
    isFloat = true,
    isDarkTheme = false,
    contentKeyboard,
    themeLightScroll = false,
    extraClasses = ''
  ): void {
    if (this.keyboardRef) {
      this.keyboardRef.instance.type = type;
      this.keyboardRef.instance.inputReference = inputReference;
      this.keyboardRef.instance.currentFormCtr = currentControl;
      this.keyboardRef.instance.showNavigateBar = showNavigateBar;
      this.keyboardRef.instance.nameList = nameList;
      this.keyboardRef.instance.isFloat = isFloat;
      this.keyboardRef.instance.isDarkTheme = isDarkTheme;
      this.keyboardRef.instance.themeLightScroll = themeLightScroll;
      const existingDomElem = (this.keyboardRef.hostView as EmbeddedViewRef<any>)
        .rootNodes[0] as HTMLElement;
      extraClasses
        .split(' ')
        .filter(Boolean)
        .forEach((cls) => existingDomElem.classList.add(cls));
    } else {
      const componentFactory =
        this.componentFactoryResolver.resolveComponentFactory(AmKeyboardComponent);
      this.keyboardRef = componentFactory.create(this.injector);
      this.keyboardRef.instance.type = type;
      this.keyboardRef.instance.inputReference = inputReference;
      this.keyboardRef.instance.currentFormCtr = currentControl;
      this.keyboardRef.instance.showNavigateBar = showNavigateBar;
      this.keyboardRef.instance.nameList = nameList;
      this.keyboardRef.instance.themeLightScroll = themeLightScroll;
      this.appRef.attachView(this.keyboardRef.hostView);
      const domElem = (this.keyboardRef.hostView as EmbeddedViewRef<any>)
        .rootNodes[0] as HTMLElement;
      extraClasses
        .split(' ')
        .filter(Boolean)
        .forEach((cls) => domElem.classList.add(cls));
      this.keyboardRef.instance.isFloat = isFloat;
      this.keyboardRef.instance.isDarkTheme = isDarkTheme;
      if (this.acceptList.includes(type)) {
        if (isFloat) {
          const element: HTMLElement = document.createElement('div');
          this.divRoot = element;
          element.appendChild(domElem);
          document.body.appendChild(element);
        } else {
          const displayKeyboard = document.getElementById(contentKeyboard);
          displayKeyboard.appendChild(domElem);
          this.divRoot = displayKeyboard;
        }
      }
      this.isShow = true;
    }
  }
  /**
    Oculta el teclado virtual sin destruirlo.
    @return void
  **/
  hide() {
    this.divRoot.style.display = 'none';
    this.isShow = false;
  }
  /**
    Navega al siguiente elemento en el teclado virtual.
    @return void
  **/
  next() {
    this.keyboardRef?.instance?.onNext();
  }
}
